import { Injectable } from '@angular/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

export interface CacheStats {
  totalSizeBytes: number
  totalSizeMB: number
  uuidFolders: string[]
  totalFolders: number
  totalFiles: number
}

export interface DeleteResult {
  totalDeleted: number
  totalFailed: number
  failedFolders: string[]
}

@Injectable({
  providedIn: 'root',
})
export class CacheManagementService {
  private readonly UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  constructor() {}

  /**
   * Scans the Cache directory and returns statistics about cache usage
   */
  async getCacheStats(): Promise<CacheStats> {
    try {
      const dirCache = await Filesystem.readdir({ path: '', directory: Directory.Cache })
      let totalSize = 0
      const uuidFolders: string[] = []
      let totalFolders = 0
      let totalFiles = 0

      for (const file of dirCache.files) {
        if (file.type === 'directory') {
          totalFolders++
          if (this.UUID_REGEX.test(file.name)) {
            uuidFolders.push(file.name)
            // Calculate size of UUID folders
            const folderSize = await this.getFolderSize(file.name, Directory.Cache)
            totalSize += folderSize
          }
        } else if (file.type === 'file') {
          totalFiles++
          if (file.size) {
            totalSize += file.size
          }
        }
      }

      return {
        totalSizeBytes: totalSize,
        totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
        uuidFolders,
        totalFolders,
        totalFiles,
      }
    } catch (error) {
      console.error('Error getting cache stats:', error)
      return {
        totalSizeBytes: 0,
        totalSizeMB: 0,
        uuidFolders: [],
        totalFolders: 0,
        totalFiles: 0,
      }
    }
  }

  /**
   * Scans Cache directory and returns only UUID folder names
   */
  async scanForUuidFolders(): Promise<string[]> {
    try {
      const dirCache = await Filesystem.readdir({ path: '', directory: Directory.Cache })
      const uuidFolders: string[] = []

      for (const file of dirCache.files) {
        if (file.type === 'directory' && this.UUID_REGEX.test(file.name)) {
          uuidFolders.push(file.name)
        }
      }

      return uuidFolders
    } catch (error) {
      console.error('Error scanning for UUID folders:', error)
      return []
    }
  }

  /**
   * Recursively calculates the size of a folder
   */
  async getFolderSize(folderPath: string, directory: Directory): Promise<number> {
    try {
      const dirResult = await Filesystem.readdir({ path: folderPath, directory })
      let totalSize = 0

      for (const file of dirResult.files) {
        if (file.type === 'file' && file.size) {
          totalSize += file.size
        } else if (file.type === 'directory') {
          const subFolderSize = await this.getFolderSize(`${folderPath}/${file.name}`, directory)
          totalSize += subFolderSize
        }
      }

      return totalSize
    } catch (error) {
      console.error(`Error getting folder size for ${folderPath}:`, error)
      return 0
    }
  }

  /**
   * Deletes specified UUID folders from Cache directory
   * @param folderNames Array of folder names to delete
   * @param onProgress Optional callback to report progress
   */
  async deleteUuidFolders(folderNames: string[], onProgress?: (deleted: number, total: number) => void): Promise<DeleteResult> {
    let totalDeleted = 0
    let totalFailed = 0
    const failedFolders: string[] = []

    for (const folderName of folderNames) {
      try {
        await Filesystem.rmdir({
          path: folderName,
          directory: Directory.Cache,
          recursive: true,
        })
        totalDeleted++
        if (onProgress) {
          onProgress(totalDeleted, folderNames.length)
        }
      } catch (error) {
        totalFailed++
        failedFolders.push(folderName)
        console.error(`Failed to delete folder ${folderName}:`, error)
      }
    }

    return {
      totalDeleted,
      totalFailed,
      failedFolders,
    }
  }

  /**
   * Deletes all UUID folders from Cache directory
   * @param onProgress Optional callback to report progress
   */
  async deleteAllUuidFolders(onProgress?: (deleted: number, total: number) => void): Promise<DeleteResult> {
    const uuidFolders = await this.scanForUuidFolders()
    return await this.deleteUuidFolders(uuidFolders, onProgress)
  }

  /**
   * Checks if a folder name matches UUID pattern
   */
  isUuidFolder(folderName: string): boolean {
    return this.UUID_REGEX.test(folderName)
  }
}
